import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const convertedStatuses = ['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped']
const notConvertedStatuses = ['inactive', 'Did Not Join']

const convertedStatusSet = new Set(convertedStatuses.map((value) => String(value).trim().toLowerCase()))
const notConvertedStatusSet = new Set(notConvertedStatuses.map((value) => String(value).trim().toLowerCase()))

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const normalizeText = (value) => String(value || '').trim()
const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const formatPercent = (value) => {
  const safeValue = Number.isFinite(value) ? value : 0
  return `${safeValue.toFixed(1)}%`
}

const CourseTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="courseBySourceTooltip">
      <strong>{label}</strong>
      <div className="courseBySourceTooltipRow">
        <span>Applicants</span>
        <strong>{formatCount(row.applicants)}</strong>
      </div>
      <div className="courseBySourceTooltipRow">
        <span>Converted</span>
        <strong>{formatCount(row.converted)}</strong>
      </div>
      <div className="courseBySourceTooltipRow">
        <span>Not converted</span>
        <strong>{formatCount(row.notConverted)}</strong>
      </div>
      <div className="courseBySourceTooltipRow">
        <span>Conversion rate</span>
        <strong>{formatPercent(row.conversionRate)}</strong>
      </div>
    </div>
  )
}

function CourseBySource() {
  const db = getFirestore(app)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const querySnapshot = await getDocs(collection(db, 'users'))
      const courseCounts = {}

      querySnapshot.forEach((docItem) => {
        const data = docItem.data()
        const course = normalizeText(data.course) || 'Unspecified'
        const status = normalizeStatus(data.userStatus) || 'inactive'

        if (!courseCounts[course]) {
          courseCounts[course] = {
            course,
            applicants: 0,
            converted: 0,
            notConverted: 0
          }
        }

        if (status === 'inactive') {
          courseCounts[course].applicants += 1
        } else if (convertedStatusSet.has(status)) {
          courseCounts[course].converted += 1
        } else if (notConvertedStatusSet.has(status)) {
          courseCounts[course].notConverted += 1
        }
      })

      const formatted = Object.values(courseCounts)
        .map((row) => {
          const total = row.applicants + row.converted + row.notConverted
          return {
            ...row,
            total,
            conversionRate: total > 0 ? (row.converted / total) * 100 : 0
          }
        })
        .filter((row) => row.total > 0)
        .sort((first, second) => {
          if (second.total !== first.total) {
            return second.total - first.total
          }

          return first.course.localeCompare(second.course)
        })

      setRows(formatted)
    } catch (fetchError) {
      console.error('Failed to load course by source analysis:', fetchError)
      setRows([])
      setError('We could not load the course analysis right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return rows
    }

    return rows.filter((row) => row.course.toLowerCase().includes(search))
  }, [rows, searchTerm])

  const chartData = useMemo(() => filteredRows.slice(0, 8), [filteredRows])

  const stats = useMemo(() => {
    return rows.reduce(
      (accumulator, row) => {
        accumulator.total += row.total
        accumulator.applicants += row.applicants
        accumulator.converted += row.converted
        accumulator.notConverted += row.notConverted
        return accumulator
      },
      { total: 0, applicants: 0, converted: 0, notConverted: 0 }
    )
  }, [rows])

  const overallConversionRate = stats.total > 0 ? (stats.converted / stats.total) * 100 : 0
  const topCourses = rows.slice(0, 5)
  const topCourse = rows[0] || null
  const topConversionCourse = rows.reduce((current, row) => {
    if (!current || row.conversionRate > current.conversionRate) {
      return row
    }
    return current
  }, null)
  const hasRows = filteredRows.length > 0
  const emptyMessage = rows.length
    ? 'No courses match the current search.'
    : 'No course records were found yet.'

  return (
    <div className="mainContainer courseBySourcePage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="courseBySourceContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Course Performance by Source</h4>
              <p className="appsSubtext">
                Compare applicant volume, conversions, and drop-offs across courses in one cleaner view.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total records</span>
                <strong>{formatCount(stats.total)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Applicants</span>
                <strong>{formatCount(stats.applicants)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Converted</span>
                <strong>{formatCount(stats.converted)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Conversion rate</span>
                <strong>{formatPercent(overallConversionRate)}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard courseBySourceFiltersCard">
            <div className="appsSearch input-group courseBySourceSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search course..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="courseBySourceFiltersMeta">
              <span>{formatCount(filteredRows.length)} courses visible</span>
              <span>{formatCount(chartData.length)} shown in the chart</span>
            </div>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="courseBySourceSummaryGrid">
            <div className="courseBySourceSummaryCard">
              <span>Leading course</span>
              <strong>{topCourse ? topCourse.course : '—'}</strong>
              <p>{topCourse ? `${formatCount(topCourse.total)} tracked records` : 'No data yet'}</p>
            </div>
            <div className="courseBySourceSummaryCard">
              <span>Best conversion</span>
              <strong>{topConversionCourse ? topConversionCourse.course : '—'}</strong>
              <p>{topConversionCourse ? formatPercent(topConversionCourse.conversionRate) : 'No data yet'}</p>
            </div>
            <div className="courseBySourceSummaryCard">
              <span>Applicants</span>
              <strong>{formatCount(stats.applicants)}</strong>
              <p>Users still in the applicant stage.</p>
            </div>
            <div className="courseBySourceSummaryCard">
              <span>Drop-offs</span>
              <strong>{formatCount(stats.notConverted)}</strong>
              <p>Did not join or unresolved records.</p>
            </div>
          </div>

          <div className="courseBySourceGrid">
            <div className="appsTableCard courseBySourceChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                  Course mix
                </h6>
                <span>Top courses by volume</span>
              </div>
              <div className="courseBySourceChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading course analysis...</span>
                    </Spinner>
                    <p>Loading course analysis...</p>
                  </div>
                ) : hasRows ? (
                  <div className="courseBySourceChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                        <XAxis
                          dataKey="course"
                          tick={{ fontSize: 12, fill: '#475569' }}
                          axisLine={{ stroke: '#cbd5e1' }}
                          tickLine={false}
                          interval={0}
                          angle={-18}
                          textAnchor="end"
                          height={72}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                        <Tooltip content={<CourseTooltip />} />
                        <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                        <Bar dataKey="applicants" stackId="course" name="Applicants" fill="#94a3b8" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="converted" stackId="course" name="Converted" fill="#0f766e" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="notConverted" stackId="course" name="Did Not Convert" fill="#ef4444" radius={[0, 10, 10, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>{emptyMessage}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="appsInsightsCard courseBySourceInsightsCard">
              <div className="appsInsightsHeader">
                <h6>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Course highlights
                </h6>
                <span>{formatCount(topCourses.length)} shown</span>
              </div>

              <div className="appsInsightsList">
                {topCourses.length ? (
                  topCourses.map((row) => (
                    <div className="appsInsightsItem" key={row.course}>
                      <div>
                        <strong>{row.course}</strong>
                        <span>{formatCount(row.total)} tracked records</span>
                      </div>
                      <small>{formatPercent(row.conversionRate)} converted</small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <p>No course highlights match the current search.</p>
                  </div>
                )}
              </div>

              <div className="courseBySourceMiniStats">
                <div className="courseBySourceMiniStat">
                  <span>Top course</span>
                  <strong>{topCourse ? topCourse.course : '—'}</strong>
                  <small>{topCourse ? formatCount(topCourse.total) : 'No data'}</small>
                </div>
                <div className="courseBySourceMiniStat">
                  <span>Best conversion</span>
                  <strong>{topConversionCourse ? formatPercent(topConversionCourse.conversionRate) : '0.0%'}</strong>
                  <small>{topConversionCourse ? topConversionCourse.course : 'No data'}</small>
                </div>
              </div>

              <p className="appsInsightsMore">
                Search a course to focus the view, or refresh after new records are added.
              </p>
            </div>
          </div>

          <div className="appsTableCard courseBySourceTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Course summary
              </h6>
              <span>{formatCount(filteredRows.length)} results</span>
            </div>
            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading course summary...</span>
                  </Spinner>
                  <p>Loading course summary...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable courseBySourceTable">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Applicants</th>
                      <th>Converted</th>
                      <th>Did Not Convert</th>
                      <th>Total</th>
                      <th>Conversion Rate</th>
                      <th>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasRows ? (
                      filteredRows.map((row) => {
                        const progress = topCourse?.total > 0 ? (row.total / topCourse.total) * 100 : 0

                        return (
                          <tr key={row.course}>
                            <td>
                              <div className="courseBySourceRowLabel">
                                <strong>{row.course}</strong>
                                <span>{row.total > 0 ? 'Tracked course' : 'No records'}</span>
                              </div>
                            </td>
                            <td>{formatCount(row.applicants)}</td>
                            <td>{formatCount(row.converted)}</td>
                            <td>{formatCount(row.notConverted)}</td>
                            <td>
                              <span className="courseBySourceTotalCell">{formatCount(row.total)}</span>
                            </td>
                            <td>{formatPercent(row.conversionRate)}</td>
                            <td>
                              <div className="courseBySourceTrack" aria-hidden="true">
                                <span className="courseBySourceTrackFill" style={{ width: `${Math.min(progress, 100)}%` }} />
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CourseBySource
